import { useEffect, useMemo, useRef, useState } from 'react';
import { Header } from 'pages/HomePublic/components/Header';
import { useNavigate } from 'react-router-dom';
import { useModal } from 'context/ModalContext/Modal.context';
import { FaUser, FaCheck } from 'react-icons/fa';
import { BsEmojiSmile } from 'react-icons/bs';
import { MdBookmark, MdBookmarkBorder, MdEdit } from 'react-icons/md';
import { IoCloudDownloadOutline, IoChevronDown } from 'react-icons/io5';
import ModalWarning from 'components/ui/ModalWarning';
import { VotacaoEtapa1TutorialModal } from 'components/VotacaoEtapa1TutorialModal';
import { getCategoriaCorHex } from 'utils/iniciativa-categoria';
import { participantesVisiveis } from 'utils/participantes-validacao';
import { clearRegistrationUser } from 'utils/fns';
import { useAuth } from 'context/AuthContext';
import { useActivePhases } from 'hooks/useControlDate';
import { fetchAllSelecoesByPrefixo, SelecaoEtapa1 } from 'services/selecao-etapa1.service';
import { getTutorialByTitle } from 'services/tutorial.service';
import {
  buscarValidacaoEtapa1PorPrefixo,
  ComiteMembro,
  fetchComiteMembrosByCategoriaPrefixo,
  getIdentifierFromPlayerFields,
  IniciativaEtapa1,
} from 'services/validacao-etapa1.service';
import { getEtapa1Label } from 'utils/etapa1-categoria-map';
import { Tutorial } from 'types/tutorial';
import avisoInscricao from 'assets/img/2026/ELBB/aviso_inscricao.png';
import './style.scss';

const COMPLIMENT_OPTIONS = ['Arrasou!', 'Iniciativa inspiradora!', 'Superou expectativas!'];

interface SelecaoComIniciativas {
  selecao: SelecaoEtapa1;
  iniciativas: IniciativaEtapa1[];
  historico: SelecaoEtapa1[];
  comiteMembros: ComiteMembro[];
}

const formatDateTime = (timestamp: number): string => {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  }).format(new Date(timestamp));
};

const formatComiteDisplayName = (nome: string, matricula?: string): string => {
  const nomeBase = (nome || 'Não informado')
    .replace(/\s*\(F?\d+(?:\s*-\s*[^)]+)?\)\s*$/i, '')
    .trim();
  const nomeFinal = nomeBase || 'Não informado';
  const matriculaLimpa = matricula ? clearRegistrationUser(String(matricula)) : '';
  const chave = matriculaLimpa ? `F${matriculaLimpa}` : '';

  return chave ? `${nomeFinal} (${chave})` : nomeFinal;
};

export function MeusVotos() {
  const { user } = useAuth();
  const { showModal, closeModal } = useModal();
  const navigate = useNavigate();
  const { etapa1premio } = useActivePhases();
  const identifier = useMemo(() => getIdentifierFromPlayerFields(user?.detail), [user]);
  const hasIdentifier = Boolean(identifier.comite || identifier.prefixo);
  const escopoLabel = identifier.comite ? 'comitê' : 'prefixo';

  const [selecoes, setSelecoes] = useState<SelecaoComIniciativas[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [semSelecao, setSemSelecao] = useState(false);
  const [tutorialEtapa1, setTutorialEtapa1] = useState<Tutorial | null>(null);

  const [temaSelecionado, setTemaSelecionado] = useState('Todos os temas');
  const [busca, setBusca] = useState('');
  const [historicoAbertoPorCategoria, setHistoricoAbertoPorCategoria] = useState<Set<string>>(new Set());
  const [complimentOpenId, setComplimentOpenId] = useState<string | null>(null);
  const [selectedCompliments, setSelectedCompliments] = useState<Record<string, string>>({});
  const [favoritedIds, setFavoritedIds] = useState<Set<string>>(new Set());
  const complimentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    async function carregarDados() {
      if (!hasIdentifier) {
        setLoading(false);
        return;
      }

      try {
        const todasSelecoes = await fetchAllSelecoesByPrefixo(identifier);
        const selecoesPorCategoria = todasSelecoes.reduce((acc, selecaoAtual) => {
          const categoriaKey = selecaoAtual.categoria || selecaoAtual._id;
          const historicoCategoria = acc.get(categoriaKey) || [];
          historicoCategoria.push(selecaoAtual);
          acc.set(categoriaKey, historicoCategoria);
          return acc;
        }, new Map<string, SelecaoEtapa1[]>());

        if (selecoesPorCategoria.size === 0) {
          setSemSelecao(true);
          setLoading(false);
          return;
        }

        const etapa1Player: string[] = Array.isArray(user?.detail?.etapa1) ? user.detail.etapa1 : [];
        const temMad = etapa1Player
          .map((v) => String(v).toUpperCase())
          .includes('MAD');

        const [iniciativasBase, iniciativasMad] = await Promise.all([
          buscarValidacaoEtapa1PorPrefixo(identifier),
          temMad
            ? buscarValidacaoEtapa1PorPrefixo(identifier, { category: 'MAD' })
            : Promise.resolve([] as IniciativaEtapa1[]),
        ]);
        const todasIniciativas = [...iniciativasBase, ...iniciativasMad];

        const selecoesBase = Array.from(selecoesPorCategoria.values()).map((historico) => {
          const selecaoAtual = historico[0];
          const selectedIds = new Set(selecaoAtual.iniciativaIds);
          return {
            selecao: selecaoAtual,
            iniciativas: todasIniciativas.filter((i) => selectedIds.has(i.id)),
            historico,
          };
        });

        const selecoesComIniciativas: SelecaoComIniciativas[] = await Promise.all(
          selecoesBase.map(async (item) => {
            const prefixoSelecao = item.selecao.prefixo || identifier.prefixo || '';
            const comiteMembros = item.selecao.categoria
              ? await fetchComiteMembrosByCategoriaPrefixo(item.selecao.categoria, prefixoSelecao)
              : [];
            return { ...item, comiteMembros };
          })
        );

        setSelecoes(selecoesComIniciativas);
        setSemSelecao(false);
      } catch {
        setSemSelecao(false);
        setErro('Erro ao carregar dados da seleção.');
      } finally {
        setLoading(false);
      }
    }

    carregarDados();
  }, [identifier, hasIdentifier, escopoLabel, user]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (complimentRef.current && !complimentRef.current.contains(event.target as Node)) {
        setComplimentOpenId(null);
      }
    };

    if (complimentOpenId) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [complimentOpenId]);

  useEffect(() => {
    async function carregarTutorialEtapa1() {
      try {
        const tutorialEtapa1Api = await getTutorialByTitle('TUTORIAL_ETAPA1');
        if (tutorialEtapa1Api) {
          setTutorialEtapa1(tutorialEtapa1Api);
        }
      } catch {
        setTutorialEtapa1(null);
      }
    }

    carregarTutorialEtapa1();
  }, []);

  const todasIniciativas = useMemo(
    () => selecoes.flatMap((s) => s.iniciativas),
    [selecoes]
  );

  const temas = useMemo(
    () => ['Todos os temas', ...Array.from(new Set(todasIniciativas.map((item) => item.tema)))],
    [todasIniciativas]
  );

  const filterIniciativas = (iniciativas: IniciativaEtapa1[]) => {
    const termo = busca.trim().toLowerCase();
    return iniciativas.filter((item) => {
      const matchTema = temaSelecionado === 'Todos os temas' || item.tema === temaSelecionado;
      const matchBusca = termo.length === 0 || item.titulo.toLowerCase().includes(termo);
      return matchTema && matchBusca;
    });
  };

  const handleComplimentSelect = (iniciativaId: string, compliment: string) => {
    setSelectedCompliments((prev) => ({ ...prev, [iniciativaId]: compliment }));
    setComplimentOpenId(null);
  };

  const handleToggleFavorite = (id: string) => {
    setFavoritedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleToggleHistorico = (categoriaKey: string) => {
    setHistoricoAbertoPorCategoria((prev) => {
      const next = new Set(prev);
      if (next.has(categoriaKey)) {
        next.delete(categoriaKey);
      } else {
        next.add(categoriaKey);
      }
      return next;
    });
  };

  const handleEditarSelecao = (selecao: SelecaoEtapa1) => {
    if (!etapa1premio) return;
    if (!selecao.categoria) {
      showModal(
        'meus-votos-edicao-invalida',
        <ModalWarning
          title="Não foi possível editar"
          description="Não foi possível identificar a categoria desta seleção para edição."
          btnText="Fechar"
          onClose={closeModal}
        />,
        'meus-votos-edicao-invalida-wrapper',
        false
      );
      return;
    }

    showModal(
      'meus-votos-confirmar-edicao',
      <ModalWarning
        title="Editar seleção"
        description="Deseja editar esta seleção? Você será redirecionado para a página de seleção com as iniciativas já selecionadas."
        btnText="Editar"
        hasBackButton={true}
        backBtnText="Cancelar"
        onBackButton={closeModal}
        onClose={() => {
          closeModal();
          navigate('/votacao-etapa-1', {
            state: {
              editMode: true,
              selecaoId: selecao._id,
              categoria: selecao.categoria,
              iniciativaIds: selecao.iniciativaIds,
            },
          });
        }}
      />,
      'meus-votos-confirmar-edicao-wrapper',
      false
    );
  };

  const handleOpenVotingTutorial = () => {
    if (!etapa1premio) return;
    if (!tutorialEtapa1?.steps?.length) {
      return;
    }

    showModal(
      'votacao-etapa1-tutorial-modal',
      <VotacaoEtapa1TutorialModal
        steps={tutorialEtapa1.steps}
        onClose={closeModal}
        onStartVoting={() => {
          closeModal();
          navigate('/votacao-etapa-1');
        }}
      />,
      'votacao-etapa1-tutorial-wrapper',
      false
    );
  };

  const handleGoToEtapaUmVotacao = () => {
    if (!etapa1premio) return;
    navigate('/votacao-etapa-1');
  };

  return (
    <div id="votacao-etapa-1-wrapper" className="meus-votos-page">
      <Header currentPage="meus-votos" />

      <section className="meus-votos-hero" role="banner" aria-label="Meus votos no Prêmio BB 2026">
        <div className="meus-votos-hero__content">
          <h1 className="meus-votos-hero__title">
            <span>Seleção na Etapa 1 do </span>
            <strong>Prêmio BB 2026</strong>
          </h1>
          <p className="meus-votos-hero__description">Confira aqui as iniciativas selecionadas</p>
        </div>
      </section>

      <main className="votacao-etapa-1-main meus-votos-main">
        <section className="votacao-etapa-1-content" aria-live="polite">
          {loading && (
            <p className="votacao-etapa-1-list__empty">Carregando dados da seleção...</p>
          )}

          {!loading && erro && (
            <div className="votacao-etapa-1-empty-tab" role="alert">
              <h2 className="votacao-etapa-1-empty-tab__title">{erro}</h2>
            </div>
          )}

          {!loading && !erro && semSelecao && (
            <section className="notification-banner notification-banner--votacao">
              <div className="notification-banner__content">
                <img
                  src={avisoInscricao}
                  alt="Aviso"
                  className="notification-banner__icon"
                />
                <p className="notification-banner__text">
                  Você faz parte do comitê que seleciona as iniciativas na Etapa 1 do Prêmio BB.{' '}
                  <button
                    className="notification-banner__link"
                    onClick={handleOpenVotingTutorial}
                    type="button"
                  >
                    Saiba como fazer a seleção!
                  </button>
                </p>
                <button
                  className="notification-banner__btn"
                  onClick={handleGoToEtapaUmVotacao}
                  type="button"
                >
                  INICIAR SELEÇÃO!
                </button>
              </div>
            </section>
          )}

          {!loading && !erro && selecoes.length > 0 && (
            <>
              <div className="votacao-etapa-1-filters meus-votos-main__filters">
                <select
                  value={temaSelecionado}
                  onChange={(event) => setTemaSelecionado(event.target.value)}
                  className="votacao-etapa-1-filters__select"
                >
                  {temas.map((tema) => (
                    <option key={tema} value={tema}>
                      {tema}
                    </option>
                  ))}
                </select>
                <input
                  type="search"
                  value={busca}
                  onChange={(event) => setBusca(event.target.value)}
                  placeholder="O que você procura?"
                  className="votacao-etapa-1-filters__search"
                />
              </div>

              {selecoes.map(({ selecao, iniciativas: inics, historico, comiteMembros }) => {
                const filtradas = filterIniciativas(inics);
                const categoriaLabel = selecao.categoria
                  ? getEtapa1Label(selecao.categoria)
                  : 'Seleção';
                const categoriaKey = selecao.categoria || selecao._id;
                const historicoAberto = historicoAbertoPorCategoria.has(categoriaKey);
                const edicoesAnteriores = historico.slice(1);
                const integrantesComite = comiteMembros.length > 0
                  ? comiteMembros.map((membro) => ({
                      key: membro._id || membro.chave || membro.nome,
                      nome: membro.nome_guerra || membro.nome,
                      matricula: clearRegistrationUser(String(membro._id || '')),
                      prefixo: membro.prefixo !== undefined && membro.prefixo !== null
                        ? String(membro.prefixo)
                        : (selecao.prefixo || identifier.prefixo || ''),
                      diretoria: membro.diretoria || '',
                    }))
                  : Array.from(
                      new Map(
                        historico
                          .filter((item) => item.selectedByName)
                          .map((item) => [item.selectedByMatricula || item.selectedByName, item])
                      ).values()
                    ).map((item) => ({
                      key: item.selectedByMatricula || item.selectedByName,
                      nome: item.selectedByName,
                      matricula: item.selectedByMatricula,
                      prefixo: item.prefixo || selecao.prefixo || identifier.prefixo || '',
                      diretoria: '',
                    }));

                return (
                  <div key={selecao._id} className="mb-8">
                    <div className="meus-votos-main__heading">
                      <div className="flex items-center justify-between gap-3">
                        <h2 className="meus-votos-main__title">
                          {selecoes.length > 1
                            ? `Seleção — ${categoriaLabel}`
                            : 'Seleção de iniciativas do Comitê Executivo'}
                        </h2>
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            className="text-xs font-title font-bold uppercase text-blue-bb-1 underline hover:no-underline"
                            onClick={() => handleToggleHistorico(categoriaKey)}
                          >
                            {historicoAberto ? 'Ocultar histórico' : 'Histórico'}
                          </button>
                          {etapa1premio && (
                            <button
                              type="button"
                              className="inline-flex items-center gap-1.5 bg-yellow-bb text-blue-bb-1 rounded-[4px] px-3 py-2 text-xs font-title font-bold uppercase hover:brightness-95 transition-all"
                              onClick={() => handleEditarSelecao(selecao)}
                            >
                              <MdEdit size={14} aria-hidden="true" />
                              EDITAR
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                        <div className="border border-[#E0E7FF] rounded-md p-3 bg-[#F8FAFF]">
                          <span className="block text-[11px] uppercase tracking-wide text-[#6B7280] font-semibold">Responsável pela seleção</span>
                          <span className="block text-sm text-[#111214] font-medium mt-1">{selecao.selectedByName}</span>
                        </div>
                        <div className="border border-[#E0E7FF] rounded-md p-3 bg-[#F8FAFF]">
                          <span className="block text-[11px] uppercase tracking-wide text-[#6B7280] font-semibold">Data e hora</span>
                          <span className="block text-sm text-[#111214] font-medium mt-1">{formatDateTime(selecao.selectedAt)}</span>
                        </div>
                        <div className="border border-[#E0E7FF] rounded-md p-3 bg-[#F8FAFF]">
                          <span className="block text-[11px] uppercase tracking-wide text-[#6B7280] font-semibold">Iniciativas selecionadas</span>
                          <span className="block text-sm text-[#111214] font-medium mt-1">{selecao.totalSelecionadas} de {selecao.totalIniciativas}</span>
                        </div>
                      </div>
                      <div className="border border-[#E0E7FF] rounded-md p-3 bg-[#F8FAFF] mt-3">
                        <span className="block text-[11px] uppercase tracking-wide text-[#6B7280] font-semibold">Justificativa</span>
                        <span className="block text-sm text-[#111214] font-medium mt-1">{selecao.justificativa}</span>

                        {integrantesComite.length > 0 && (
                          <div className="mt-3">
                            <span className="block text-[11px] uppercase tracking-wide text-[#6B7280] font-semibold">Integrantes do comitê</span>
                            {(integrantesComite[0].prefixo || integrantesComite[0].diretoria) && (
                              <span className="block text-sm text-[#111214] font-semibold mt-0.5">
                                {[integrantesComite[0].prefixo, integrantesComite[0].diretoria].filter(Boolean).join(' — ')}
                              </span>
                            )}
                            <div className="votacao-etapa-1-card__participantes-grid mt-2" role="list" aria-label="Integrantes do comitê">
                              {integrantesComite.map((membro) => (
                                <div
                                  key={`${membro.key}`}
                                  className="votacao-etapa-1-card__participante confirmado"
                                  role="listitem"
                                >
                                  <div className="votacao-etapa-1-card__participante-content">
                                    <FaUser className="votacao-etapa-1-card__participante-avatar" size={14} aria-hidden="true" />
                                    <FaCheck className="votacao-etapa-1-card__participante-check" size={14} aria-hidden="true" />
                                    <span className="votacao-etapa-1-card__participante-nome">
                                      {formatComiteDisplayName(membro.nome, membro.matricula)}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {historicoAberto && (
                        <div className="border border-[#E0E7FF] rounded-md p-3 bg-white mt-3">
                          <div className="mt-3 border border-[#E0E7FF] rounded-md bg-white p-3">
                            <h3 className="text-[11px] uppercase tracking-wide text-[#6B7280] font-semibold mb-2">Histórico de edições</h3>

                            <div className="border border-[#E0E7FF] rounded-md p-2 bg-[#F8FAFF] mb-2">
                              <span className="block text-[11px] uppercase tracking-wide text-[#6B7280] font-semibold">Última seleção</span>
                              <span className="block text-sm text-[#111214] font-medium mt-1">{historico[0]?.selectedByName}</span>
                              <span className="block text-xs text-[#6B7280] mt-1">{formatDateTime(historico[0]?.selectedAt || 0)}</span>
                              <span className="block text-sm text-[#111214] mt-2">{historico[0]?.justificativa}</span>
                            </div>

                            {edicoesAnteriores.length === 0 && (
                              <p className="text-sm text-[#6B7280]">Não há edições anteriores para esta seleção.</p>
                            )}

                            {edicoesAnteriores.map((edicao) => (
                              <div key={edicao._id} className="border border-[#E0E7FF] rounded-md p-2 mb-2 last:mb-0">
                                <span className="block text-[11px] uppercase tracking-wide text-[#6B7280] font-semibold">
                                  Edição anterior
                                </span>
                                <span className="block text-sm text-[#111214] font-medium mt-1">{edicao.selectedByName}</span>
                                <span className="block text-xs text-[#6B7280] mt-1">{formatDateTime(edicao.selectedAt)}</span>
                                <span className="block text-sm text-[#111214] mt-2">{edicao.justificativa}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="votacao-etapa-1-list">
                      {filtradas.length === 0 && (
                        <p className="votacao-etapa-1-list__empty">Nenhuma iniciativa encontrada para os filtros selecionados.</p>
                      )}

                      {filtradas.map((iniciativa) => {
                  const corCategoriaHex = getCategoriaCorHex(iniciativa.categoryPremio);
                  const { lista: participantesVis, numerador: confirmados, denominador: totalParticipantes } =
                    participantesVisiveis(iniciativa.participantes, {
                      ativo: true,
                      validadeApurada: iniciativa.validadeApurada,
                      totalValidos: iniciativa.totalValidos,
                      totalOriginal: iniciativa.totalOriginal,
                    });
                  const selectedCompliment = selectedCompliments[iniciativa.id] || '';
                  const isFavorited = favoritedIds.has(iniciativa.id);
                  const isComplimentOpen = complimentOpenId === iniciativa.id;

                  return (
                    <article key={iniciativa.id} className="votacao-etapa-1-card is-selected">
                      <div className="votacao-etapa-1-card__body">
                        <div className="votacao-etapa-1-card__left">
                          <div className="votacao-etapa-1-card__video-container">
                            {iniciativa.videoUrl ? (
                              <a
                                href={iniciativa.videoUrlOriginal || iniciativa.videoUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="votacao-etapa-1-card__video-link"
                              >
                                <img src={iniciativa.imagem} alt={iniciativa.titulo} className="votacao-etapa-1-card__video-thumbnail" />
                              </a>
                            ) : (
                              <img src={iniciativa.imagem} alt={iniciativa.titulo} className="votacao-etapa-1-card__video-thumbnail" />
                            )}
                          </div>

                          {iniciativa.documentacao && (
                            <div className="votacao-etapa-1-card__documentacao">
                              <p className="votacao-etapa-1-card__documentacao-label">Documentação complementar da realização</p>
                              <button className="votacao-etapa-1-card__download-btn" type="button">
                                <IoCloudDownloadOutline size={20} aria-hidden="true" />
                                <span>DOWNLOAD</span>
                              </button>
                            </div>
                          )}

                          {(iniciativa.elogiosPorTipo.length > 0 || iniciativa.totalFavoritos > 0) && (
                            <div className="votacao-etapa-1-card__feedback-box">
                              <h3 className="votacao-etapa-1-card__feedback-title">Feedback na iniciativa:</h3>
                              <div className="votacao-etapa-1-card__feedback-list">
                                {iniciativa.elogiosPorTipo.map((item, index) => (
                                  <div key={index} className="votacao-etapa-1-card__feedback-item">
                                    <span className="votacao-etapa-1-card__feedback-count">{String(item.quantidade).padStart(2, '0')}</span>
                                    <span className="votacao-etapa-1-card__feedback-label">{item.elogio}</span>
                                  </div>
                                ))}
                                {iniciativa.totalFavoritos > 0 && (
                                  <div className="votacao-etapa-1-card__feedback-item">
                                    <span className="votacao-etapa-1-card__feedback-count">{String(iniciativa.totalFavoritos).padStart(2, '0')}</span>
                                    <span className="votacao-etapa-1-card__feedback-label">Salvos nas favoritas</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="votacao-etapa-1-card__right">
                          <div className="votacao-etapa-1-card__header">
                            <p className="votacao-etapa-1-card__tema">{iniciativa.tema.toUpperCase()}</p>
                            <div
                              className={`votacao-etapa-1-card__badge tag-${iniciativa.corCategoria}`}
                              style={{ backgroundColor: corCategoriaHex }}
                            >
                              {iniciativa.categoriaTag}
                            </div>
                          </div>

                          <h3 className="votacao-etapa-1-card__title">{iniciativa.titulo}</h3>

                          <div className="votacao-etapa-1-card__info-box">
                            <div className="votacao-etapa-1-card__info-item">
                              <span className="votacao-etapa-1-card__info-label">Cadastrado por:</span>
                              <span className="votacao-etapa-1-card__info-value">
                                {iniciativa.cadastradoPor}, em {iniciativa.cadastradoData}
                              </span>
                            </div>
                            <div className="votacao-etapa-1-card__info-item">
                              <span className="votacao-etapa-1-card__info-label">Inscrito por:</span>
                              <span className="votacao-etapa-1-card__info-value">
                                {iniciativa.inscritoPor}, em {iniciativa.inscritoData}
                              </span>
                            </div>
                          </div>

                          <div className="votacao-etapa-1-card__question">
                            <h3 className="votacao-etapa-1-card__question-title">{iniciativa.resumo}</h3>
                            <p className="votacao-etapa-1-card__question-text">{iniciativa.resumoTexto}</p>
                          </div>

                          <div className="votacao-etapa-1-card__participantes" role="region" aria-label="Participantes da realização">
                            <h3 className="votacao-etapa-1-card__participantes-title">
                              Participantes da realização ({confirmados} de {totalParticipantes} confirmados)
                            </h3>
                            <div className="votacao-etapa-1-card__participantes-grid" role="list">
                              {participantesVis.map((participante, idx) => (
                                <div
                                  key={`${participante.nome}-${idx}`}
                                  className={`votacao-etapa-1-card__participante ${
                                    participante.confirmado || participante.isOwner ? 'confirmado' : 'pendente'
                                  }`}
                                  role="listitem"
                                >
                                  <div className="votacao-etapa-1-card__participante-content">
                                    <FaUser className="votacao-etapa-1-card__participante-avatar" size={14} aria-hidden="true" />
                                    {(participante.confirmado || participante.isOwner) && (
                                      <FaCheck className="votacao-etapa-1-card__participante-check" size={14} aria-hidden="true" />
                                    )}
                                    <span className="votacao-etapa-1-card__participante-nome">
                                      {participante.nome} ({participante.matricula})
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="votacao-etapa-1-card__action-bar">
                            <div className="votacao-etapa-1-card__action-left">
                              <div className="votacao-etapa-1-card__icon-wrapper">
                                <BsEmojiSmile
                                  size={20}
                                  aria-hidden="true"
                                  style={{ color: selectedCompliment ? '#5D5FEF' : '#666666' }}
                                />
                                {iniciativa.totalElogios > 0 && (
                                  <span className="votacao-etapa-1-card__badge-count">{iniciativa.totalElogios}</span>
                                )}
                              </div>

                              <div className="votacao-etapa-1-card__compliment-container" ref={isComplimentOpen ? complimentRef : undefined}>
                                <button
                                  className="votacao-etapa-1-card__compliment-dropdown"
                                  onClick={() => setComplimentOpenId(isComplimentOpen ? null : iniciativa.id)}
                                  aria-expanded={isComplimentOpen}
                                  aria-haspopup="listbox"
                                  type="button"
                                  style={{
                                    borderColor: selectedCompliment ? '#5D5FEF' : '#E0E7FF',
                                    color: selectedCompliment ? '#5D5FEF' : '#666666',
                                  }}
                                >
                                  <span>{selectedCompliment || 'Faça um elogio!'}</span>
                                  <IoChevronDown size={16} aria-hidden="true" />
                                </button>
                                {isComplimentOpen && (
                                  <div className="votacao-etapa-1-card__compliment-menu" role="listbox">
                                    {COMPLIMENT_OPTIONS.map((compliment) => (
                                      <button
                                        key={compliment}
                                        className="votacao-etapa-1-card__compliment-option"
                                        role="option"
                                        aria-selected={selectedCompliment === compliment}
                                        onClick={() => handleComplimentSelect(iniciativa.id, compliment)}
                                        type="button"
                                      >
                                        {compliment}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="votacao-etapa-1-card__action-right">
                              <div className="votacao-etapa-1-card__icon-wrapper">
                                <button
                                  className="votacao-etapa-1-card__action-icon-btn"
                                  aria-label={isFavorited ? 'Remover dos favoritos' : 'Salvar nos favoritos'}
                                  aria-pressed={isFavorited}
                                  onClick={() => handleToggleFavorite(iniciativa.id)}
                                  style={{ color: isFavorited ? '#5D5FEF' : '#666666' }}
                                  type="button"
                                >
                                  {isFavorited ? <MdBookmark size={20} aria-hidden="true" /> : <MdBookmarkBorder size={20} aria-hidden="true" />}
                                </button>
                                {iniciativa.totalFavoritos > 0 && (
                                  <span className="votacao-etapa-1-card__badge-count">{iniciativa.totalFavoritos}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </section>
      </main>
    </div>
  );
}
